import { useState, useRef, useCallback } from 'react'
import {
  Upload,
  X,
  File,
  FileText,
  FileImage,
  FileVideo,
  FileAudio,
  FileArchive,
  FileCode,
  Download,
  CheckCircle,
  AlertCircle,
  Clock,
  Loader2,
  Trash2,
  Send,
  FolderOpen
} from 'lucide-react'

interface FileTransferData {
  id: string
  name: string
  size: number
  type: string
  status: 'pending' | 'uploading' | 'downloading' | 'completed' | 'failed'
  progress: number
  speed?: number
  timeRemaining?: number
  error?: string
  sender?: string
  timestamp: Date
}

interface FileTransferUIProps {
  isOpen: boolean
  onClose: () => void
  onFileSelect: (files: File[]) => void
  activeTransfers?: FileTransferData[]
  maxFileSize?: number // in MB
  allowedTypes?: string[]
  multiple?: boolean
}

export function FileTransferUI({
  isOpen,
  onClose,
  onFileSelect,
  activeTransfers = [],
  maxFileSize = 100,
  allowedTypes,
  multiple = true
}: FileTransferUIProps) {
  const [dragActive, setDragActive] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [errors, setErrors] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s`
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
  }

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return <FileImage className="w-5 h-5" />
    if (type.startsWith('video/')) return <FileVideo className="w-5 h-5" />
    if (type.startsWith('audio/')) return <FileAudio className="w-5 h-5" />
    if (type.includes('zip') || type.includes('rar') || type.includes('7z'))
      return <FileArchive className="w-5 h-5" />
    if (type.includes('javascript') || type.includes('typescript') || type.includes('json'))
      return <FileCode className="w-5 h-5" />
    if (type.includes('text') || type.includes('pdf') || type.includes('doc'))
      return <FileText className="w-5 h-5" />
    return <File className="w-5 h-5" />
  }

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxFileSize * 1024 * 1024) {
      return `File "${file.name}" exceeds maximum size of ${maxFileSize}MB`
    }

    // Check file type if restrictions exist
    if (allowedTypes && allowedTypes.length > 0) {
      const isAllowed = allowedTypes.some(type => {
        if (type.includes('*')) {
          const baseType = type.split('/')[0]
          return baseType ? file.type.startsWith(baseType) : false
        }
        return file.type === type
      })

      if (!isAllowed) {
        return `File type "${file.type}" is not allowed`
      }
    }

    return null
  }

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const files = Array.from(e.dataTransfer.files)
    handleFiles(files)
  }, [])

  const handleFiles = (files: File[]) => {
    const newErrors: string[] = []
    const validFiles: File[] = []

    files.forEach(file => {
      const error = validateFile(file)
      if (error) {
        newErrors.push(error)
      } else {
        validFiles.push(file)
      }
    })

    setErrors(newErrors)

    if (!multiple && validFiles.length > 0) {
      const firstFile = validFiles[0]
      if (firstFile) {
        setSelectedFiles([firstFile])
      }
    } else {
      setSelectedFiles(prev => [...prev, ...validFiles])
    }
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(Array.from(e.target.files))
    }
  }

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleSend = () => {
    if (selectedFiles.length > 0) {
      onFileSelect(selectedFiles)
      setSelectedFiles([])
      setErrors([])
      onClose()
    }
  }

  const getTransferStatus = (transfer: FileTransferData) => {
    switch (transfer.status) {
      case 'uploading':
        return (
          <div className="flex items-center space-x-2 text-blue-600">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Uploading...</span>
          </div>
        )
      case 'downloading':
        return (
          <div className="flex items-center space-x-2 text-blue-600">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Downloading...</span>
          </div>
        )
      case 'completed':
        return (
          <div className="flex items-center space-x-2 text-success-600">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">Completed</span>
          </div>
        )
      case 'failed':
        return (
          <div className="flex items-center space-x-2 text-error-600">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">Failed</span>
          </div>
        )
      default:
        return (
          <div className="flex items-center space-x-2 text-secondary-600">
            <Clock className="w-4 h-4" />
            <span className="text-sm">Pending</span>
          </div>
        )
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-secondary-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-secondary-900">File Transfer</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-secondary-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-secondary-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Drop Zone */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
              dragActive
                ? 'border-primary-500 bg-primary-50'
                : 'border-secondary-300 hover:border-secondary-400'
            }`}
          >
            <Upload className="w-12 h-12 text-secondary-400 mx-auto mb-4" />
            <p className="text-secondary-700 font-medium mb-2">
              Drag & drop files here, or click to browse
            </p>
            <p className="text-sm text-secondary-500 mb-4">
              Maximum file size: {maxFileSize}MB
              {allowedTypes && ` • Allowed types: ${allowedTypes.join(', ')}`}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple={multiple}
              onChange={handleFileInput}
              accept={allowedTypes?.join(',')}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="btn btn-outline"
            >
              <FolderOpen className="w-4 h-4 mr-2" />
              Browse Files
            </button>
          </div>

          {/* Error Messages */}
          {errors.length > 0 && (
            <div className="mt-4 p-3 bg-error-50 border border-error-200 rounded-lg">
              {errors.map((error, index) => (
                <div key={index} className="flex items-start space-x-2 text-sm text-error-700">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              ))}
            </div>
          )}

          {/* Selected Files */}
          {selectedFiles.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-secondary-700 mb-3">
                Selected Files ({selectedFiles.length})
              </h3>
              <div className="space-y-2">
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg"
                  >
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="text-secondary-600">
                        {getFileIcon(file.type)}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-secondary-900 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-secondary-500">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFile(index)}
                      className="p-1 hover:bg-secondary-200 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-secondary-600" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Active Transfers */}
          {activeTransfers.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-secondary-700 mb-3">
                Active Transfers ({activeTransfers.length})
              </h3>
              <div className="space-y-3">
                {activeTransfers.map(transfer => (
                  <div key={transfer.id} className="p-4 bg-secondary-50 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-start space-x-3 min-w-0">
                        <div className="text-secondary-600">
                          {getFileIcon(transfer.type)}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-secondary-900 truncate">
                            {transfer.name}
                          </p>
                          <p className="text-xs text-secondary-500">
                            {formatFileSize(transfer.size)}
                            {transfer.sender && ` • From: ${transfer.sender}`}
                          </p>
                        </div>
                      </div>
                      {getTransferStatus(transfer)}
                    </div>

                    {/* Progress Bar */}
                    {(transfer.status === 'uploading' || transfer.status === 'downloading') && (
                      <>
                        <div className="w-full bg-secondary-200 rounded-full h-2 overflow-hidden">
                          <div
                            className="h-full bg-primary-500 transition-all duration-300"
                            style={{ width: `${transfer.progress}%` }}
                          />
                        </div>
                        <div className="mt-2 flex items-center justify-between text-xs text-secondary-600">
                          <span>{transfer.progress}%</span>
                          {transfer.speed && (
                            <span>{formatFileSize(transfer.speed)}/s</span>
                          )}
                          {transfer.timeRemaining && (
                            <span>{formatTime(transfer.timeRemaining)} remaining</span>
                          )}
                        </div>
                      </>
                    )}

                    {/* Error Message */}
                    {transfer.error && (
                      <p className="mt-2 text-xs text-error-600">{transfer.error}</p>
                    )}

                    {/* Download Button for Completed Received Files */}
                    {transfer.status === 'completed' && transfer.sender && (
                      <button className="mt-2 flex items-center space-x-1 text-sm text-primary-600 hover:text-primary-700">
                        <Download className="w-4 h-4" />
                        <span>Download</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-secondary-200 flex items-center justify-between">
          <p className="text-sm text-secondary-600">
            {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
            {selectedFiles.length > 0 &&
              ` (${formatFileSize(selectedFiles.reduce((acc, f) => acc + f.size, 0))})`}
          </p>
          <div className="flex items-center space-x-3">
            <button onClick={onClose} className="btn btn-outline">
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={selectedFiles.length === 0}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Files
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}